// apps/web/src/lib/gantt-adapter.ts — Fase 03, Plano 01 (OPS-05, D-01).
// Adapter React sobre dhtmlx-gantt Community (v10.0.1, MIT).
// Portado e adaptado do spike — não importa de spikes/ (verify:import-boundaries).
import { Gantt, type GanttStatic, type Task, type ZoomLevel } from 'dhtmlx-gantt';
import 'dhtmlx-gantt/codebase/dhtmlxgantt.css';
import type { GanttTask, GanttLink } from '@pieltec/contracts';

export interface WebGanttData {
  data: GanttTask[];
  links: GanttLink[];
}

export interface GanttAdapterApi {
  destroy: () => void;
  loadData: (data: WebGanttData) => void;
  zoomIn: () => void;
  zoomOut: () => void;
}

const ZOOM_LEVELS: ZoomLevel[] = [
  {
    name: 'dia',
    scale_height: 50,
    min_column_width: 40,
    scales: [{ unit: 'day', step: 1, format: '%d %M' }],
  },
  {
    name: 'semana',
    scale_height: 50,
    min_column_width: 50,
    scales: [
      { unit: 'week', step: 1, format: 'S%W' },
      { unit: 'day', step: 1, format: '%d' },
    ],
  },
  {
    name: 'mês',
    scale_height: 50,
    min_column_width: 75,
    scales: [{ unit: 'month', step: 1, format: '%M %Y' }],
  },
];

export function initGantt(
  container: HTMLElement,
  data: WebGanttData,
  onTaskSelect?: (taskId: string | null) => void,
): GanttAdapterApi {
  const instance: GanttStatic = Gantt.getGanttInstance();

  instance.config.date_format = '%Y-%m-%d';
  instance.config.xml_date = '%Y-%m-%d';
  instance.config.readonly = true; // D-01: Visualização do snapshot finita-consciente
  instance.config.drag_move = false;
  instance.config.drag_progress = false;
  instance.config.drag_resize = false;
  instance.config.drag_links = false;
  instance.config.touch = 'force'; // Suporte touch em tablet
  instance.config.touch_drag = 75;

  instance.config.columns = [
    { name: 'text', label: 'Ordem de Produção / Etapa', width: '*', tree: true },
    { name: 'start_date', label: 'Início', align: 'center', width: 90 },
    { name: 'duration', label: 'Dias', align: 'center', width: 60 },
  ];

  // Customização de templates para destacar conflitos e status
  instance.templates.task_class = (_start: Date, _end: Date, task: Task) => {
    const customTask = task as unknown as GanttTask;
    const classes: string[] = [];
    if (customTask.conflito) {
      classes.push('gantt-task-conflito border-dashed border-red-500');
    }
    if (customTask.type === 'project') {
      classes.push('gantt-task-project bg-slate-700 text-white font-semibold');
    }
    return classes.join(' ');
  };

  instance.ext.zoom.init({
    levels: ZOOM_LEVELS,
  });
  instance.ext.zoom.setLevel('semana');

  if (onTaskSelect) {
    instance.attachEvent('onTaskSelected', (id: string | number) => {
      onTaskSelect(String(id));
      return true;
    });
    instance.attachEvent('onEmptyClick', () => {
      onTaskSelect(null);
      return true;
    });
  }

  instance.init(container);
  instance.parse(data);

  return {
    destroy: () => {
      try {
        instance.clearAll();
      } catch {
        // Ignora se o elemento container já foi desmontado pelo React
      }
    },
    loadData: (newData: WebGanttData) => {
      instance.clearAll();
      instance.parse(newData);
    },
    zoomIn: () => {
      instance.ext.zoom.zoomIn();
    },
    zoomOut: () => {
      instance.ext.zoom.zoomOut();
    },
  };
}
